/**
 * Image canvas — viewer for `kind: "image"` artifacts.
 *
 * Renders the image centered on a checkered backdrop so transparent PNGs
 * read correctly, with a floating zoom pill, a dimensions badge, and a
 * collapsible right sidebar that surfaces file + generation metadata.
 *
 * v1 deliberately ships without the variants strip and re-prompt bar from
 * the design mock — those rely on lineage columns (parent id, prompt,
 * model, seed) and an image-gen tool that don't exist yet.
 *
 * @module components/artifacts/image-canvas
 */

import { motion } from "framer-motion";
import {
  Copy,
  Download,
  ExternalLink,
  Grid2x2,
  ImageIcon,
  Loader2,
  Maximize2,
  Minus,
  PanelRight,
  Plus,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatAspectRatio,
  formatImageSize,
  formatLabel,
  parseImageArtifactContent,
  type ImageArtifactContent,
} from "@/lib/artifacts/image";
import { useArtifactStore } from "@/store/artifact-store";
import { api } from "@/utils/api-client";
import { ArtifactErrorBanner } from "./artifact-error-banner";
import { FocusModeToggle } from "./focus-mode-toggle";
import { ImageArtifact, type ImageBackdropMode } from "./image-artifact";

/** Shape of the `/api/artifacts/:id` response we read here. */
interface FetchedArtifact {
  id: string;
  title: string;
  content: string;
  updatedAt?: string;
}

/** Bounds + step for the zoom pill. Keeps the user from zooming into a single pixel. */
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.1;

/** Initial zoom for new images — `0` is the marker meaning "fit to canvas". */
const ZOOM_FIT = 0;

/**
 * Standalone image canvas mounted by the kind dispatcher in `artifact.tsx`.
 * Reads the active artifact from the global store; nothing about the image
 * payload is plumbed via props.
 */
export function ImageCanvas() {
  const artifact = useArtifactStore((s) => s.artifact);
  const setArtifact = useArtifactStore((s) => s.setArtifact);
  const hideArtifact = useArtifactStore((s) => s.hideArtifact);

  const [fetchedAt, setFetchedAt] = useState<string | undefined>(undefined);
  const [zoom, setZoom] = useState<number>(ZOOM_FIT);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [backdrop, setBackdrop] = useState<ImageBackdropMode>("checkered");

  const isStreaming = artifact.status === "streaming";
  const parsed = useMemo(
    () => parseImageArtifactContent(artifact.content),
    [artifact.content],
  );

  /**
   * Hydrate the canonical record on open. Streamed deltas reach the store
   * first, but a fresh open after a reload needs the persisted `content`
   * since the data stream is gone.
   */
  useEffect(() => {
    if (!artifact.isVisible || !artifact.id || artifact.status === "streaming") return;
    let cancelled = false;
    const fetchId = artifact.id;
    api
      .get(`/artifacts/${fetchId}`)
      .then((response) => {
        if (cancelled) return;
        // Skip a late response for an artifact the user switched away from.
        if (useArtifactStore.getState().artifact.id !== fetchId) return;
        const fetched = (response as { data?: FetchedArtifact })?.data;
        if (!fetched) return;
        setFetchedAt(fetched.updatedAt);
        if (fetched.content && fetched.content !== artifact.content) {
          setArtifact({ content: fetched.content, title: fetched.title });
        }
      })
      .catch(() => {
        // Silent — the store may already have streamed content.
      });
    return () => {
      cancelled = true;
    };
  }, [artifact.isVisible, artifact.id, artifact.status, artifact.content, setArtifact]);

  const handleDownload = useCallback(async () => {
    if (!parsed?.url) return;
    try {
      const response = await fetch(parsed.url);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filenameFor(artifact.title, parsed.mimeType);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download image");
    }
  }, [parsed, artifact.title]);

  const handleCopy = useCallback(async () => {
    if (!parsed?.url) return;
    try {
      await navigator.clipboard.writeText(parsed.url);
      toast.success("Image URL copied");
    } catch {
      toast.error("Could not copy URL");
    }
  }, [parsed]);

  const handleOpenInNewTab = useCallback(() => {
    if (!parsed?.url) return;
    window.open(parsed.url, "_blank", "noopener,noreferrer");
  }, [parsed]);

  const handleFullscreen = useCallback(() => {
    if (!parsed?.url) return;
    window.open(parsed.url, "_blank", "noopener,noreferrer");
  }, [parsed]);

  const zoomIn = useCallback(
    () =>
      setZoom((z) =>
        Math.min(ZOOM_MAX, (z === ZOOM_FIT ? 1 : z) + ZOOM_STEP),
      ),
    [],
  );
  const zoomOut = useCallback(
    () =>
      setZoom((z) =>
        Math.max(ZOOM_MIN, (z === ZOOM_FIT ? 1 : z) - ZOOM_STEP),
      ),
    [],
  );
  const setFit = useCallback(() => setZoom(ZOOM_FIT), []);
  const setActual = useCallback(() => setZoom(1), []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 80, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.96 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative flex h-full min-w-0 flex-[3] flex-col border-l bg-background shadow-2xl max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:flex-none max-lg:border-l-0"
    >
      <header className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2.5">
        <div className="size-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <ImageIcon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold leading-tight">
            {artifact.title || "Untitled"}
          </h2>
          <SubtitleRow
            fetchedAt={fetchedAt}
            isStreaming={isStreaming}
            kindLabel={parsed ? formatLabel(parsed.mimeType) : "Image"}
          />
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleCopy}
          disabled={!parsed?.url}
          title="Copy image URL"
        >
          <Copy className="size-5" />
        </Button>
        <FocusModeToggle />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleDownload}
          disabled={!parsed?.url}
          title="Download image"
        >
          <Download className="size-5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleOpenInNewTab}
          disabled={!parsed?.url}
          title="Open in new tab"
        >
          <ExternalLink className="size-5" />
        </Button>
        <div className="mx-1 h-5 w-px bg-border" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen((open) => !open)}
          aria-pressed={sidebarOpen}
          title={sidebarOpen ? "Hide details" : "Show details"}
          className={cn(sidebarOpen && "bg-muted")}
        >
          <PanelRight className="size-5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleFullscreen}
          disabled={!parsed?.url}
          title="Open at full size"
        >
          <Maximize2 className="size-5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={hideArtifact}
          title="Close"
        >
          <X className="size-5" />
        </Button>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Canvas */}
        <div className="relative flex-1 overflow-hidden">
          <ImageArtifact
            url={isStreaming ? undefined : parsed?.url}
            alt={artifact.title}
            width={parsed?.width}
            height={parsed?.height}
            zoom={zoom}
            backdrop={backdrop}
            className="absolute inset-0"
          />

          {isStreaming || !parsed ? (
            <GenerationSkeleton hasContent={Boolean(parsed)} />
          ) : null}

          {/* Dimensions badge — top-left */}
          {parsed?.width && parsed?.height ? (
            <div className="pointer-events-none absolute top-3 left-3 rounded-md border border-border/70 bg-background/85 px-2.5 py-1 text-xs font-medium tabular-nums text-foreground shadow-sm backdrop-blur">
              {parsed.width} × {parsed.height}
            </div>
          ) : null}

          {/* Floating zoom pill — bottom-center */}
          {parsed ? (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full border border-border/70 bg-background/90 px-1.5 py-1.5 shadow-lg backdrop-blur">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={zoomOut}
                title="Zoom out"
                disabled={zoom !== ZOOM_FIT && zoom <= ZOOM_MIN + 0.01}
              >
                <Minus className="size-4" />
              </Button>
              <div className="min-w-[3.25rem] px-1 text-center text-xs font-medium tabular-nums text-foreground">
                {zoom === ZOOM_FIT ? "Fit" : `${Math.round(zoom * 100)}%`}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={zoomIn}
                title="Zoom in"
                disabled={zoom !== ZOOM_FIT && zoom >= ZOOM_MAX - 0.01}
              >
                <Plus className="size-4" />
              </Button>
              <div className="mx-1 h-4 w-px bg-border" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "h-7 rounded-full px-2.5 text-xs",
                  zoom === ZOOM_FIT && "bg-muted",
                )}
                onClick={setFit}
              >
                Fit
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "h-7 rounded-full px-2.5 text-xs",
                  zoom === 1 && "bg-muted",
                )}
                onClick={setActual}
              >
                100%
              </Button>
              <div className="mx-1 h-4 w-px bg-border" />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={() =>
                  setBackdrop((mode) =>
                    mode === "checkered"
                      ? "light"
                      : mode === "light"
                        ? "dark"
                        : "checkered",
                  )
                }
                title="Toggle backdrop"
              >
                <Grid2x2 className="size-4" />
              </Button>
            </div>
          ) : null}
        </div>

        {/* Sidebar */}
        {sidebarOpen ? (
          <DetailsSidebar
            image={parsed}
            fetchedAt={fetchedAt}
            onClose={() => setSidebarOpen(false)}
          />
        ) : null}
      </div>

      {/* Error/retry banner — surfaces a failed image generation (issue #961). */}
      <ArtifactErrorBanner />
    </motion.div>
  );
}

/**
 * Right-aligned metadata sidebar. Sections collapse gracefully — anything
 * the producer didn't fill in just doesn't render, so the panel stays terse
 * for hand-uploaded images and verbose for AI-generated ones.
 */
function DetailsSidebar({
  image,
  fetchedAt,
  onClose,
}: {
  image: ImageArtifactContent | null;
  fetchedAt: string | undefined;
  onClose: () => void;
}) {
  const formatBadge = image ? formatLabel(image.mimeType) : null;
  const aspect = image ? formatAspectRatio(image.width, image.height) : null;
  const sizeLabel = image ? formatImageSize(image.sizeBytes) : null;

  return (
    <aside className="w-[320px] shrink-0 overflow-y-auto border-l border-border bg-background">
      <div className="sticky top-0 flex items-center justify-between border-b border-border bg-background px-4 py-3">
        <div className="text-sm font-semibold">Details</div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onClose}
          title="Hide details"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="space-y-6 p-4">
        {/* File */}
        <SidebarSection title="File">
          {image?.width && image?.height ? (
            <SidebarRow label="Dimensions" value={`${image.width} × ${image.height}`} mono />
          ) : null}
          {aspect ? <SidebarRow label="Aspect" value={aspect} /> : null}
          {formatBadge ? <SidebarRow label="Format" value={formatBadge} /> : null}
          {sizeLabel ? <SidebarRow label="Size" value={sizeLabel} mono /> : null}
        </SidebarSection>

        {/* Generation — only when at least one lineage field is present */}
        {image && (image.model || image.seed !== undefined) ? (
          <SidebarSection title="Generation">
            {image.model ? (
              <SidebarRow
                label="Model"
                value={
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                    {image.model}
                  </span>
                }
              />
            ) : null}
            {image.seed !== undefined ? (
              <SidebarRow label="Seed" value={image.seed} mono />
            ) : null}
            {fetchedAt ? (
              <SidebarRow label="Created" value={updatedLabel(fetchedAt)} />
            ) : null}
          </SidebarSection>
        ) : fetchedAt ? (
          <SidebarSection title="Updated">
            <SidebarRow label="Last update" value={updatedLabel(fetchedAt)} />
          </SidebarSection>
        ) : null}

        {/* Prompt */}
        {image?.prompt ? (
          <section>
            <SidebarHeader title="Prompt" />
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm leading-relaxed text-foreground/90">
              {image.prompt}
            </p>
          </section>
        ) : null}
      </div>
    </aside>
  );
}

function SidebarSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <SidebarHeader title={title} />
      <dl className="space-y-2 text-sm">{children}</dl>
    </section>
  );
}

function SidebarHeader({ title }: { title: string }) {
  return (
    <div className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
      {title}
    </div>
  );
}

function SidebarRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("text-right", mono && "tabular-nums")}>{value}</dd>
    </div>
  );
}

/**
 * Skeleton shown before the image payload is parseable. While streaming we
 * trust that a finish event will arrive; without parseable content we tell
 * the user the artifact is empty so they don't stare at a blank pane.
 */
function GenerationSkeleton({ hasContent }: { hasContent: boolean }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
      {hasContent ? (
        <>
          <Loader2 className="size-8 animate-spin text-primary" />
          <div className="text-sm text-muted-foreground">Loading image…</div>
        </>
      ) : (
        <>
          <ImageIcon className="size-10 text-muted-foreground/50" />
          <div className="max-w-xs text-center text-sm text-muted-foreground">
            This artifact is empty. The producer hasn't supplied an image URL yet.
          </div>
        </>
      )}
    </div>
  );
}

function SubtitleRow({
  fetchedAt,
  isStreaming,
  kindLabel,
}: {
  fetchedAt: string | undefined;
  isStreaming: boolean;
  kindLabel: string;
}) {
  const parts: string[] = [kindLabel];
  if (isStreaming) {
    parts.push("Generating…");
  } else if (fetchedAt) {
    parts.push(`Updated ${updatedLabel(fetchedAt)}`);
  }
  return (
    <span className="text-xs text-muted-foreground">{parts.join(" · ")}</span>
  );
}

/** Best-effort extension from a mime type for the saved filename. */
function extensionFor(mimeType: string): string {
  if (mimeType === "image/svg+xml") return "svg";
  return mimeType.split("/").pop() ?? "png";
}

/** Strip filesystem-unfriendly characters from the artifact title. */
function safeFilename(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return slug || "image";
}

function filenameFor(title: string, mimeType: string): string {
  return `${safeFilename(title)}.${extensionFor(mimeType)}`;
}

function updatedLabel(timestamp?: string): string {
  if (!timestamp) return "Recently";
  const updated = new Date(timestamp);
  if (Number.isNaN(updated.getTime())) return "Recently";
  const diffMs = Date.now() - updated.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return updated.toLocaleDateString();
}
